import React from 'react';
import { useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowBranchFlow from './components/WorkflowBranchFlow';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowOutputBox from './components/WorkflowOutputBox';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';

const reqSteps = [
  { label: 'Part1', title: 'RFPを作成する', path: '/devdocs/workflow/req-step1' },
  { label: 'Part2', title: 'AI対話で仕様を策定する', path: '/devdocs/workflow/req-step2' },
  {
    label: 'Part3',
    title: 'proposal/businessを出力する',
    path: '/devdocs/workflow/req-step3',
    active: true,
  },
  { label: 'Part4', title: 'システムユースケースを出力する', path: '/devdocs/workflow/req-step4' },
];

const WorkflowReqStep3: React.FC = () => {
  const { hash } = useLocation();

  React.useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      // DOMレンダリング完了後にスクロールするため少し遅延
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hash]);

  return (
    <WorkflowArticleLayout
      title="要件定義編 Part3 — proposal/businessを出力する"
      seriesName="要件定義編"
      tags={['Requirements', 'proposal', 'business']}
      seriesSteps={reqSteps}
      prevLink={{ label: 'Part2: AI対話で仕様策定', path: '/devdocs/workflow/req-step2' }}
      nextLink={{ label: 'Part4: usecases出力', path: '/devdocs/workflow/req-step4' }}
    >
      {/* この記事で学べること */}
      <WorkflowLearningPoints
        items={[
          'proposal.md（技術仕様）とbusiness.md（ビジネスユースケース）の役割と違い',
          'Steeringにフォーマット・記載ルールを定義する方法',
          '一括生成と段階的生成の使い分け',
          'ファイル内注釈（note記法）による修正指示の出し方',
          '提出前チェックリストによる品質確認',
        ]}
      />

      {/* 目次 */}
      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '概要' },
          { id: 'section-2', label: '成果物1: proposal.md' },
          { id: 'section-3', label: '成果物2: business.md' },
          { id: 'section-4', label: '出力手順' },
          { id: 'section-5', label: '修正指示の出し方' },
          { id: 'section-6', label: '画面チームの利用方法' },
          { id: 'section-7', label: 'よくあるミスとリカバリ' },
          { id: 'section-8', label: '提出前チェックリスト' },
          { id: 'section-9', label: 'ベストプラクティス' },
          { id: 'section-10', label: 'アンチパターン' },
          { id: 'section-11', label: '成果物まとめ' },
        ]}
      />

      {/* 1. 概要 */}
      <WorkflowSection title="概要" id="section-1" number={1}>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            [
              '前提条件',
              'Part2の対話が完了し、RFPの「決定事項」セクションにテーブル設計・API設計・ビジネスルール・認証認可が記載されている。決定事項に曖昧な部分が残っていない',
            ],
            [
              '完了条件',
              'proposal.md と business.md が生成されている。提出前チェックリストの全項目に合格している',
            ],
          ]}
        />
        <WorkflowText>
          Part2の対話で蓄積された決定事項を、2種類のドキュメントに整理・出力します。
          この2ファイルは後続工程で
          usecases.md（システムユースケース）を経由し、バックエンド成果物・画面成果物の両方へ展開されます。
        </WorkflowText>

        {/* 3段構成フローチャート */}
        <Stack spacing={1} sx={{ mt: 2 }}>
          {/* Part3 出力 */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              Part3 出力
            </Typography>
            <WorkflowBranchFlow
              input={{ label: 'RFP（決定事項）', sub: '対話で蓄積された仕様' }}
              outputs={[
                { label: 'proposal.md', sub: '決定事項', highlight: true },
                { label: 'business.md', sub: 'ビジネスユースケース', highlight: true },
              ]}
            />
          </Box>

          {/* Part4 出力 */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              Part4 出力
            </Typography>
            <WorkflowBranchFlow
              input={{ label: 'usecases.md', sub: 'システムユースケース', highlight: true }}
              outputs={[
                { label: 'proposal.md', sub: '決定事項' },
                { label: 'business.md', sub: 'ビジネスユースケース' },
              ]}
              reverse
            />
          </Box>

          {/* 最終成果物 */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              最終成果物
            </Typography>
            <Stack spacing={2}>
              <WorkflowBranchFlow
                input={{
                  label: 'バックエンド成果物',
                  sub: 'スキーマ定義 / API仕様書 / API一覧 / 権限一覧 / プロパティ一覧',
                  highlight: true,
                }}
                outputs={[{ label: 'proposal.md' }, { label: 'usecases.md' }]}
                reverse
              />
              <WorkflowBranchFlow
                input={{
                  label: '画面成果物',
                  sub: '画面仕様書 / 入出力項目 / 操作手順書',
                  highlight: true,
                }}
                outputs={[{ label: 'business.md' }, { label: 'usecases.md' }]}
                reverse
              />
            </Stack>
          </Box>
        </Stack>

        {/* なぜファイルを分けるのか */}
        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            なぜファイルを分けるのか — 参照者の数だけ分ける
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>
            「このドキュメントを読む人は誰か？」が異なるなら、ファイルを分ける。
            1ファイルにまとめると、各チームが自分に関係ない部分を読み飛ばし、必要な情報を見落としやすくなります。
          </WorkflowText>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            本プロジェクトの例（2チーム構成 → 2ファイル）:
          </Typography>
          <WorkflowTable
            headers={['ファイル', '主な参照者', '使い方']}
            rows={[
              ['proposal.md', 'バックエンドチーム', 'スキーマ定義・API仕様書の入力として使う'],
              [
                'business.md',
                '画面チーム・QAチーム',
                '操作手順書作成・シナリオ試験の元情報として使う',
              ],
            ]}
          />
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
            チームが増える場合はファイルも増やす:
          </Typography>
          <WorkflowTable
            headers={['状況', '追加するファイル例', '記載内容']}
            rows={[
              [
                '外部API連携チームがいる',
                'external_api.md',
                '公開エンドポイント・認証方式・レート制限',
              ],
              ['バッチ運用チームがいる', 'batch.md', '実行スケジュール・リトライ方針・監視項目'],
              ['インフラチームがいる', 'infra.md', 'リソース要件・スケーリング条件・環境変数'],
            ]}
          />
          <WorkflowText sx={{ mt: 1 }}>
            参照者の数 = ファイルの数。これが分割の判断基準です。
          </WorkflowText>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* 2. 成果物1: proposal.md */}
      <WorkflowSection title="成果物1: *.proposal.md" id="section-2" number={2}>
        <WorkflowInfoBox>
          proposal.md
          は「要件定義書」と「基本設計書」の中間に位置する文書です。技術的な決定事項（テーブル設計、API設計）をまとめたもので、後続のスキーマ定義やAPI仕様書のベースになります。
        </WorkflowInfoBox>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            ['配置先', '.kiro/requirements/{機能名}/'],
            ['ファイル名', '{機能名}.proposal.md'],
            ['例', '.kiro/requirements/member_management/partner.proposal.md'],
          ]}
        />

        <WorkflowSection
          title="Steeringに定義するフォーマット（本プロジェクトの例）"
          subNumber="2.1"
        >
          <WorkflowText sx={{ mb: 1 }}>
            proposal.md
            は人間が手書きするものではなく、AIが自動生成します。出力フォーマットを揃えるために、Steeringファイル（create_proposal.md）に以下のような構造を定義しています。Front
            Matterは fileMatch で .kiro/requirements/**/*.rfp.md と
            .kiro/requirements/**/*.proposal.md を指定します。AIはこのSteeringを参照して proposal.md
            を生成するため、毎回同じ構造で出力されます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="proposal.md フォーマット（Steering定義）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`## 決定事項

### 概要
- {機能の概要、エンティティ間の関係}

### 認証・認可
- {各APIの権限要件}

### ビジネスルール
- {ドメイン固有のルール}

---

## テーブル定義

### {テーブル名}

| 論理名 | 物理名 | 種別 | Not Null | PK | 備考 |
|--------|--------|------|----------|----|----- |
| ...    | ...    | ...  | ...      | ...| ...  |

インデックス:
| index名 | column | seq |
|---------|--------|-----|
| ...     | ...    | ... |

初期データ:（マスタテーブルの場合）
| ... | ... | ... |

---

## API一覧

| API ID | API名 | Method | Endpoint | 認可 |
|--------|-------|--------|----------|------|
| ...    | ...   | ...    | ...      | ...  |

---

## API概要

### {API_ID} {API名}

- {APIの概要}
- リクエスト: {主要な入力項目}
- レスポンス: {返却内容}
- エラー: {主要なエラーケース}`}
          />

          <WorkflowInfoBox type="warning">
            このフォーマットはプロジェクトによって変わります。上記は本プロジェクト（Java / Spring
            Boot / MySQL / REST API）向けです。GraphQL
            APIなら「Query/Mutation一覧」、イベント駆動なら「イベント定義」、NoSQLなら「コレクション設計」になります。自分のプロジェクトに合わせてSteeringのフォーマットをカスタマイズしてください。
          </WorkflowInfoBox>
        </WorkflowSection>

        {/* Steeringに定義すべき記載ルール（proposal） */}
        <WorkflowSection
          title="Steeringに定義すべき記載ルール（本プロジェクトの例 — proposal）"
          subNumber="2.2"
        >
          <WorkflowText sx={{ mb: 1 }}>
            テンプレート構造だけでなく、AIが守るべき記載ルールもSteeringに定義しておきます。これにより、毎回プロンプトで注意点を指示しなくても正しく生成されます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="proposal記載ルール（Steering定義例）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`## 記載ルール

### テーブル定義
- RFPの決定事項に記載された全テーブルを含める
- カラム・型・桁数・Not Null・PKはRFPの決定事項と一致させる
- マスタテーブルには初期データを含める

### API一覧・API概要
- RFPの決定事項に記載された全APIを含める
- API IDの採番はRFPに従う（独自に採番しない）
- 認可はRFPの認証・認可セクションと一致させる

### ビジネスルール
- RFPの決定事項から転記・整理する
- 独自に追加・改変しない

## 禁止事項
- RFPに記載されていないテーブル・カラム・APIを勝手に追加しない
- Not Null・桁数をRFPと食い違わせない
- API IDを独自に採番しない`}
          />

          {/* ポイント: なぜ記載ルールをSteeringに書くのか */}
          <WorkflowInfoBox>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              ポイント: なぜ記載ルールをSteeringに書くのか
            </Typography>
            <WorkflowText sx={{ mb: 1 }}>
              記載ルールがプロンプト（チャットの指示文）にしか存在しないと、以下の問題が起きます：
            </WorkflowText>
            <WorkflowTable
              headers={['問題', '起きること']}
              rows={[
                ['毎回書く手間', '生成のたびに同じ注意事項を書く必要がある。忘れると品質が下がる'],
                [
                  'コンテキスト圧縮で消える',
                  '対話が長くなると過去の指示が圧縮され、ルールが失われる',
                ],
                ['チーム内でばらつく', 'メンバーごとに書く注意事項が異なり、出力品質が安定しない'],
              ]}
            />
            <WorkflowText sx={{ mt: 1 }}>
              Steeringに定義しておけば、誰がどのタイミングで生成しても同じルールが適用されます。
            </WorkflowText>
          </WorkflowInfoBox>
        </WorkflowSection>

        {/* 各セクションの狙い（proposal） */}
        <WorkflowSection title="各セクションの狙い（proposal）" subNumber="2.3">
          <WorkflowTable
            headers={['セクション', '狙い', '後続工程での使われ方']}
            rows={[
              [
                '決定事項 > 概要',
                '機能の全体像とエンティティ関係を1箇所にまとめる',
                'DD書（詳細設計）の冒頭説明に引用',
              ],
              [
                '決定事項 > 認証・認可',
                '権限要件を明確化し、設計漏れを防ぐ',
                'SecurityConfig・SessionFilterの設計根拠',
              ],
              [
                '決定事項 > ビジネスルール',
                'ドメイン固有の判定ロジックを明文化',
                'UseCase/Serviceの実装方針',
              ],
              [
                'テーブル定義',
                'カラム・型・制約を確定させる',
                'Flywayマイグレーション・Entity設計の入力',
              ],
              ['API一覧', 'APIの全量を俯瞰する', 'API仕様書の作成着手前に一覧確認'],
              ['API概要', '各APIの入出力・エラーを簡潔に記述', 'API仕様書の詳細設計のベース'],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* 3. 成果物2: business.md */}
      <WorkflowSection title="成果物2: *.business.md" id="section-3" number={3}>
        <WorkflowInfoBox>
          business.md は「ビジネスユースケース」=
          ユーザーがシステムをどう使うかのシナリオです。画面チームが操作手順書を作成するための元情報であり、シナリオ試験のベースにもなります。
        </WorkflowInfoBox>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            ['配置先', '.kiro/requirements/usecases/'],
            ['ファイル名', '{機能名}.business.md'],
            ['例', '.kiro/requirements/usecases/partner.business.md'],
          ]}
        />

        <WorkflowSection
          title="Steeringに定義するフォーマット（本プロジェクトの例）"
          subNumber="3.1"
        >
          <WorkflowText sx={{ mb: 1 }}>
            business.md も proposal.md
            と同様に、AIが自動生成します。Steeringファイル（create_business.md）に以下のような構造を定義しておくことで、出力フォーマットが毎回揃います。Front
            Matterは fileMatch で .kiro/requirements/**/*.rfp.md、
            .kiro/requirements/**/*.proposal.md、.kiro/requirements/usecases/*.business.md
            を指定します。
          </WorkflowText>
          <WorkflowCodeBlock
            title="business.md フォーマット（Steering定義）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# {機能名} ビジネスユースケース

## BUC-{ID}: {ユーザーが達成したいこと}

- 目的: {この操作で何を実現したいか}
- 操作者: {誰がこの操作を行うか（権限含む）}
- 前提条件: {この操作を行うために必要な状態}
- 利用API:
  - {API ID} {API名} \`{Method} {Endpoint}\`（{操作の意味}）
- 手順:
  1. {画面を開く}
  2. {入力/選択/操作}
  3. ...
- 入力項目:
  | 項目名 | 必須/任意 | 形式 | 桁数 | 備考 |
  |--------|-----------|------|------|------|
  | xxx    | 必須      | テキスト | 300 | ... |
- 結果: {操作完了後にユーザーが確認できる状態}
- 注意事項: {ユーザーが気をつけるべきこと}`}
          />

          <WorkflowInfoBox type="warning">
            このフォーマットはプロジェクトによって変わります。CLIツールなら「コマンドを実行する」、バッチ処理なら「トリガー条件・処理フロー」、API単体提供ならリクエスト/レスポンス例で手順を表現します。自分のプロジェクトに合わせてカスタマイズしてください。
          </WorkflowInfoBox>
        </WorkflowSection>

        {/* Steeringに定義すべき記載ルール（business） */}
        <WorkflowSection
          title="Steeringに定義すべき記載ルール（本プロジェクトの例 — business）"
          subNumber="3.2"
        >
          <WorkflowText sx={{ mb: 1 }}>
            business.md も同様に、記載ルールをSteeringに定義しておきます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="business記載ルール（Steering定義例）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`## 記載ルール

### 入力項目テーブル
- 全BUCに入力項目テーブルを含める（省略しない）
- 桁数は proposal.md のテーブル定義と一致させる
- 必須/任意は proposal.md の Not Null と一致させる

### 利用API
- API ID・Endpointは proposal.md のAPI一覧と一致させる
- 1BUCに対応するAPIを全て列挙する

### 操作者
- 権限は proposal.md の認証・認可と一致させる

### 注意事項
- 不可逆操作・副作用がある場合は必ず記載する

## 禁止事項
- proposal.md に存在しないAPIを利用APIに記載しない
- 入力項目の桁数を proposal.md のカラム桁数と食い違わせない
- 入力項目テーブルを省略しない`}
          />
        </WorkflowSection>

        {/* 各セクションの狙い（business） */}
        <WorkflowSection title="各セクションの狙い（business）" subNumber="3.3">
          <WorkflowTable
            headers={['セクション', '狙い', '参照チームでの使われ方']}
            rows={[
              ['目的', '1BUC = 1目的で粒度を統一する', '操作手順書のタイトル・導入文'],
              ['操作者', '権限要件を操作目線で記述する', '画面の権限制御・表示切替の根拠'],
              [
                '前提条件',
                'ユーザーがどの状態から操作を始めるかを明示',
                'テストのprecondition設計',
              ],
              [
                '利用API',
                '画面がどのAPIを呼ぶか明記する',
                '画面 → API の紐付け（実装時の迷い防止）',
              ],
              ['手順', '操作ステップを時系列で記述する', '操作手順書の骨格'],
              [
                '入力項目',
                '桁数・必須/任意・形式を画面チームに伝える',
                'フォームバリデーション設計の根拠',
              ],
              ['結果', '操作完了後に確認できる状態', 'E2Eテストの期待結果'],
              ['注意事項', '間違えやすいポイント・不可逆操作', '操作手順書の「注意」セクション'],
            ]}
          />
        </WorkflowSection>

        {/* ビジネスユースケースの書き方のポイント */}
        <WorkflowSection title="ビジネスユースケースの書き方のポイント" subNumber="3.4">
          <WorkflowTable
            headers={['ポイント', '説明']}
            rows={[
              ['ユーザー目線で書く', '技術用語（API, DB）ではなく「画面」「ボタン」「入力」で記述'],
              [
                '1BUC = 1目的',
                '「パートナーを登録する」「パートナーを検索する」のように目的単位で分ける',
              ],
              ['入力項目テーブル必須', '桁数・必須/任意・形式を明記する（画面実装に直結する情報）'],
              ['利用APIを明記', '画面チームがどのAPIを呼ぶか分かるように'],
              [
                '注意事項を忘れずに',
                '「契約終了日を設定すると一覧で非表示になる」等のユーザーが知るべきこと',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* 4. 出力手順 */}
      <WorkflowSection title="出力手順" id="section-4" number={4}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          全体フロー
        </Typography>

        {/* ビジュアルフロー図 */}
        <WorkflowProcedureFlow
          steps={[
            { label: 'Part2', variant: 'external' },
            [
              {
                label: 'proposal.md を生成',
                sub: '確認: テーブル定義・API一覧がRFPと一致しているか',
              },
              {
                label: 'business.md を生成',
                sub: '確認: 入力項目の桁数・必須任意がproposalと一致しているか',
              },
            ],
            {
              label: '整合性チェック',
              sub: 'proposal の API入出力 と business の入力項目テーブルが矛盾していないか',
              variant: 'check',
            },
            { label: '提出前チェックリストの確認', variant: 'complete' },
            { label: 'Part4', variant: 'external' },
          ]}
        />

        {/* 一括生成パターン */}
        <WorkflowSection title="指示方法: 一括生成パターン" subNumber="4.1">
          <WorkflowText sx={{ mb: 1 }}>
            Steeringファイルにフォーマット・記載ルール・禁止事項が定義済みのため、基本的にはシンプルな指示で正しく生成されます。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            #partner.rfp.md の決定事項を元に、以下の2ファイルを生成してください。{'\n\n'}
            1. .kiro/requirements/member_management/partner.proposal.md{'\n'}
            2. .kiro/requirements/usecases/partner.business.md
          </WorkflowChatBubble>
        </WorkflowSection>

        {/* 段階的生成パターン */}
        <WorkflowSection title="指示方法: 段階的生成パターン" subNumber="4.2">
          <WorkflowText sx={{ mb: 1 }}>
            確認しながら1ファイルずつ生成する場合の指示例です。慣れてきたら一括生成でも問題ありませんが、どちらの場合もセクション8「提出前チェックリスト」を必ず通してください。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            Step A: proposal.md を先に生成
          </Typography>
          <WorkflowChatBubble role="human">
            #partner.rfp.md の決定事項を元に、{'\n'}
            .kiro/requirements/member_management/partner.proposal.md を作成してください。{'\n\n'}
            テーブル定義とAPI一覧がRFPの決定事項と一致しているか確認したいので、{'\n'}
            生成後にRFPとの差分があれば教えてください。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            Step B: 確認して修正
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>
            生成されたproposal.mdを確認し、RFPの決定事項と食い違っている箇所を修正します。修正指示の出し方は2つあります（詳細はセクション5「修正指示の出し方」を参照）。
          </WorkflowText>
          <WorkflowText sx={{ mb: 0.5 }}>
            <strong>方法1: チャットで修正指示を出す</strong>（修正が1〜2箇所の場合）
          </WorkflowText>
          <WorkflowChatBubble role="human">
            proposal.md を確認しました。以下を修正してください：{'\n'}-
            partner_nameのmax桁数が100になっているが、200に修正して{'\n'}- API3005のEndpointが
            /v1/partners になっているが /v1/partner/list に修正して
          </WorkflowChatBubble>

          <WorkflowText sx={{ mt: 1, mb: 0.5 }}>
            <strong>方法2: ファイル内注釈を書き込む</strong>（修正が3箇所以上、または複雑な場合）
          </WorkflowText>
          <WorkflowChatBubble role="human">
            partner.proposal.md に注釈を書き込みました。{'\n'}
            全てのnoteに対応して修正し、注釈は削除してください。
          </WorkflowChatBubble>
          <WorkflowText sx={{ mt: 0.5 }}>
            方法2の詳細（note記法のルール定義・使い方・注意点）はセクション5で解説しています。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            Step C: business.md を生成
          </Typography>
          <WorkflowChatBubble role="human">
            修正したpartner.proposal.mdの内容に合わせて、{'\n'}
            .kiro/requirements/usecases/partner.business.md を作成してください。{'\n\n'}
            入力項目テーブルの桁数はproposal.mdのテーブル定義と一致させてください。
          </WorkflowChatBubble>
        </WorkflowSection>

        {/* 生成後の確認 */}
        <WorkflowSection title="生成後の確認" subNumber="4.3">
          <WorkflowText sx={{ mb: 0.5 }}>
            生成が完了したら、セクション8「提出前チェックリスト」の観点で確認してください。特に食い違いが起きやすいのは以下の3点です：
          </WorkflowText>
          <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
            <Box component="li">
              <Typography variant="body2" color="text.secondary">
                <strong>桁数</strong>: proposalのカラム桁数 と businessの入力項目テーブルの桁数
              </Typography>
            </Box>
            <Box component="li">
              <Typography variant="body2" color="text.secondary">
                <strong>Endpoint</strong>: proposalのAPI一覧 と businessの利用API
              </Typography>
            </Box>
            <Box component="li">
              <Typography variant="body2" color="text.secondary">
                <strong>必須/任意</strong>: proposalのNot Null と
                businessの入力項目テーブルの必須/任意
              </Typography>
            </Box>
          </Box>
          <WorkflowInfoBox>
            AIに「proposalとbusinessの整合性をチェックして」と指示すれば、AIが自動で差分を検出してくれます。自分の目だけで確認する必要はありません。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 5. 修正指示の出し方 */}
      <WorkflowSection title="修正指示の出し方" id="section-5" number={5}>
        <WorkflowText sx={{ mb: 1 }}>
          生成された proposal.md / business.md に修正が必要な場合、2つの方法があります。
        </WorkflowText>
        <WorkflowInfoBox>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            ポイント: どちらの方法を使うべきか
          </Typography>
          <WorkflowTable
            headers={['方法', '向いているケース']}
            rows={[
              [
                'チャットで指示',
                '修正が1〜2箇所、または「全体のトーンを変えて」のような広範な指示',
              ],
              [
                'ファイル内注釈',
                '修正箇所が3箇所以上に散在し各箇所で異なる修正内容がある場合。または修正内容が複雑でチャットだけでは正確に伝えきれない場合',
              ],
            ]}
          />
        </WorkflowInfoBox>

        {/* ファイル内注釈（note記法） */}
        <WorkflowSection title="ファイル内注釈（note記法）を使った修正指示" subNumber="5.1">
          <WorkflowText sx={{ mb: 1 }}>
            修正箇所が複数に散在する場合は、生成されたファイルの中に直接注釈を書き込む方法が効果的です。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            なぜこの方法が有効か
          </Typography>
          <WorkflowTable
            headers={['利点', '説明']}
            rows={[
              [
                '位置の曖昧さがゼロ',
                '修正したい箇所の直下に注釈があるため、AIが修正対象を誤認しない',
              ],
              ['複数箇所を一括指示できる', '5箇所の注釈を書けば、1回の指示で5箇所同時に修正される'],
              [
                'コンテキスト効率が良い',
                'AIがファイルを読むだけで「どこを」「どう直すか」を同時に取得できる',
              ],
              [
                '指示が永続する',
                'チャットの指示はコンテキスト圧縮で消えうるが、ファイルに書いた注釈は何度読んでも同じ',
              ],
              ['レビュー記録が残る', '注釈をgitで管理すれば「誰が何を指摘したか」の履歴が残る'],
            ]}
          />

          {/* Steeringにノート記法ルールを定義する */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
            Steeringにノート記法ルールを定義する
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>
            この方式を使うには、Steeringにノート記法のルールを定義しておきます。AIは対象ファイルを読み込んだときにSteeringも同時に読み込むため、注釈の意味を自動で理解して対応します。
          </WorkflowText>

          <WorkflowInfoBox>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              ポイント: このルールは inclusion: always で定義する
            </Typography>
            <WorkflowText sx={{ mb: 1 }}>
              note記法は proposal/business
              の修正指示だけでなく、全工程で横断的に使えます。特定のファイルパターンに限定する
              fileMatch ではなく、常時読み込まれる always
              が適切です。ただし以下の点に気をつけてください：
            </WorkflowText>
            <WorkflowTable
              headers={['気をつけること', '理由']}
              rows={[
                [
                  'ルールは軽量に保つ',
                  'always はどんな対話でも毎回コンテキストに読み込まれる。長大なルールを always にするとコンテキスト枠を常時圧迫する',
                ],
                [
                  '記法の定義だけに留める',
                  '具体的な修正基準はドメイン固有なのでfileMatchのSteeringに書く。note_rule には記法のフォーマットとAIの対応ルールだけを定義する',
                ],
                [
                  'always に追加する判断基準',
                  '「どのファイルタイプでも使う可能性があるか？」がYesならalways、特定パターンでしか使わないならfileMatch',
                ],
              ]}
            />
          </WorkflowInfoBox>

          <WorkflowCodeBlock
            title=".kiro/steering/note_rule.md"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# note_rule

## 注釈の種類

ファイル内に以下の記法で注釈を書き込む。AIはこの注釈を読んで対応する。

Rv指摘（修正が必要）:
  \`\`\`note:rv
  指摘内容
  \`\`\`

補足情報（参考程度。修正不要だが知っておくべきこと）:
  \`\`\`note:info
  補足情報
  \`\`\`

注意喚起（気を付けるべきこと）:
  \`\`\`note:warn
  気を付けるべきこと
  \`\`\`

禁止事項（やってはいけないこと）:
  \`\`\`note:alert
  やってはいけないこと
  \`\`\`

## AIの対応ルール

- note:rv → 指摘内容に従って修正し、注釈を削除する
- note:info → 内容を参考にするが、ファイルの修正は不要。注釈は削除する
- note:warn → 注意事項を踏まえて周辺の記述を見直す。注釈は削除する
- note:alert → 禁止事項に該当する記述があれば修正する。注釈は削除する`}
          />

          {/* 実際の使い方の例 */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
            実際の使い方の例
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>proposal.md に直接注釈を書き込みます：</WorkflowText>
          <WorkflowCodeBlock
            language="markdown"
            code={`### partner テーブル

| 論理名 | 物理名 | 種別 | Not Null | PK | 備考 |
|--------|--------|------|----------|----|----- |
| パートナー名 | partner_name | varchar(100) | Yes | - | - |

\`\`\`note:rv
partner_name の桁数を 100 → 200 に修正してください。
RFPの決定事項で200文字と決定済みです。
\`\`\`

## API一覧

| API ID | API名 | Method | Endpoint | 認可 |
|--------|-------|--------|----------|------|
| API3005 | パートナー一覧取得 | GET | /v1/partners | ADMIN,BACK_OFFICE,MANAGER |

\`\`\`note:rv
Endpoint を /v1/partners → /v1/partner/list に修正してください。
他のパートナーAPIと命名規則を揃えてください。
\`\`\``}
          />
          <WorkflowText sx={{ mt: 1, mb: 0.5 }}>
            書き込んだ後、チャットで以下のように指示するだけでOKです：
          </WorkflowText>
          <WorkflowChatBubble role="human">
            partner.proposal.md に注釈を書き込みました。{'\n'}
            全てのnoteに対応して修正し、注釈は削除してください。
          </WorkflowChatBubble>

          {/* 気をつけるべきこと */}
          <WorkflowInfoBox type="warning">
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              注意: ファイル内注釈を使う際の注意点
            </Typography>
            <WorkflowTable
              headers={['注意点', '説明', '対策']}
              rows={[
                [
                  '注釈の消し忘れ',
                  '修正対応後に注釈が残ったまま次工程に進むと成果物に混入する',
                  '「全てのnoteに対応して注釈を削除して」と必ず指示する',
                ],
                [
                  '注釈の記法ミス',
                  'バッククォートの数やラベル名を間違えるとAIが注釈と認識しない',
                  'Steeringで記法を厳密に定義し、コピペで書き込む',
                ],
                [
                  '注釈が多すぎる',
                  '1ファイルに20箇所以上の注釈があると、生成し直した方が早い場合がある',
                  '注釈が10箇所を超えたら「このファイルを再生成して」の方が効率的かを判断する',
                ],
                [
                  'note:info の乱用',
                  '参考情報を大量に書くとAIが本来の修正指示を見落としやすくなる',
                  '修正が必要な箇所は必ず note:rv を使う。note:info は補足のみ',
                ],
                [
                  'gitへのコミット',
                  '注釈入りの状態でコミットすると履歴が汚れる',
                  '注釈書き込み → AI修正 → 注釈削除完了 を1コミット単位にまとめる',
                ],
              ]}
            />
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 6. 画面チームの利用方法（参考） */}
      <WorkflowSection title="画面チームの利用方法（参考）" id="section-6" number={6}>
        <WorkflowText sx={{ mb: 1 }}>
          画面チームは business.md を以下のように活用します：
        </WorkflowText>
        <WorkflowTable
          headers={['business.md のセクション', '操作手順書での使い方']}
          rows={[
            ['目的', 'ガイドのタイトル・導入文に使う'],
            ['操作者', '「前提」セクションの権限説明に使う'],
            ['手順', '「手順」セクションの骨格（UI部品名を実装に合わせて具体化する）'],
            ['入力項目', '手順内の入力ステップで桁数・必須/任意を記載する'],
            ['利用API', 'API呼び出しとの整合性確認に使う（ガイドには記載しない）'],
            ['結果', '手順の最終ステップ「確認」として記載する'],
            ['注意事項', '「注意」セクションとして記載する'],
          ]}
        />
      </WorkflowSection>

      {/* 7. よくあるミスとリカバリ */}
      <WorkflowSection title="よくあるミスとリカバリ" id="section-7" number={7}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          よくあるミス（AI生成時に起きやすい問題）
        </Typography>
        <WorkflowInfoBox type="warning">
          <WorkflowTable
            headers={['AIがやりがちなこと', '何が起きるか', '防止策']}
            rows={[
              [
                'テーブル定義のNot Nullを勝手に変える',
                'RFPで「必須」と決めたのにNULL許可になる',
                '生成後に「RFPのNot Null設定と一致しているか確認して」と指示する',
              ],
              [
                '入力項目の桁数を省略する',
                '画面チームが桁数不明のまま実装してしまう',
                '「入力項目テーブルのmax桁数は全項目必ず記載して」と指示する',
              ],
              [
                'proposal と business で桁数を食い違わせる',
                '画面 200文字 → API 100文字で切り捨てエラーになる',
                '生成後に「proposalのカラム桁数とbusinessの桁数の一致を確認して」と指示する',
              ],
              [
                'Steeringのフォーマットと異なる構造で出力する',
                'セクションの抜け漏れや順序の入れ替わりが起きる',
                '「Steeringのフォーマットに従っているか確認して」と指示する',
              ],
              [
                'RFPに書いていないカラムやAPIを勝手に追加する',
                '決定していない仕様が混入し、後工程で「誰が決めた？」となる',
                '「RFPの決定事項に書いてある内容だけを記載して。追加提案は別途聞いて」と指示する',
              ],
            ]}
          />
        </WorkflowInfoBox>

        {/* リカバリ手順 */}
        <WorkflowSection title="リカバリ手順" subNumber="7.1">
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            proposal.md を修正した場合
          </Typography>
          <WorkflowText sx={{ mb: 0.5 }}>
            proposalを修正したら、business.mdも連動して確認・修正が必要です。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            partner.proposal.md のテーブル定義を修正しました。{'\n'}
            以下の変更を partner.business.md にも反映してください：{'\n'}- partner_name の桁数を 100
            → 200 に変更{'\n\n'}
            反映後、他に食い違っている箇所があれば教えてください。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            RFPの決定事項が追加された場合
          </Typography>
          <WorkflowText sx={{ mb: 0.5 }}>
            Step2に戻って対話を追加した場合、proposal/businessの再生成が必要です。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            partner.rfp.md に決定事項を追加しました。{'\n'}
            追加された内容を partner.proposal.md と partner.business.md に反映してください。{'\n'}
            既存の内容は変えずに、追加分のみ反映して。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            生成結果が大きくズレている場合
          </Typography>
          <WorkflowText sx={{ mb: 0.5 }}>
            AIの出力が期待と大きく異なる場合は、テンプレートを明示的に渡してください。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            出力がテンプレートと合っていないので、再生成してください。{'\n'}
            以下のテンプレートに従って出力して：{'\n\n'}
            （テンプレートを貼り付け）
          </WorkflowChatBubble>
        </WorkflowSection>
      </WorkflowSection>

      {/* 8. 提出前チェックリスト */}
      <WorkflowSection title="提出前チェックリスト" id="section-8" number={8}>
        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            AIが生成したドキュメントを人の目でチェックする理由
          </Typography>
          <WorkflowTable
            headers={['理由', '具体的に何が起きるか']}
            rows={[
              [
                'AIは「正しさ」を保証しない',
                'RFPで決めた桁数を200と書いたのに、AIが100で出力することがある。AIは矛盾に気づかず堂々と出力する',
              ],
              [
                '後工程の手戻りコストが大きい',
                'ここで見逃した食い違いは、API仕様書作成・実装・結合試験で発覚する。発覚が遅いほど修正コストが跳ね上がる',
              ],
              [
                'ドキュメント間の整合性はAI単独では担保しきれない',
                'proposal / business / RFP の3ファイル間の整合性は、人間が「この値で合っているか」を判断する責任がある',
              ],
              [
                '品質の最終責任は人間にある',
                'AIは道具であり、成果物の品質責任は人間が持つ。「AIが出したから正しい」は通用しない',
              ],
            ]}
          />
          <WorkflowText sx={{ mt: 1 }}>
            チェックリストを形式的に流さず、1項目ずつ「本当にそうか？」と確認してください。この工程を丁寧にやるかどうかで、後続工程の品質が決まります。
          </WorkflowText>
        </WorkflowInfoBox>

        <WorkflowInfoBox type="warning">
          以下はあくまで本プロジェクト（Java / REST API /
          MySQL）向けのチェックリストの例です。プロジェクトの技術スタック・チーム構成・ドキュメント体系によってチェック項目は変わります。自分のプロジェクト用のチェックリストを作成し、チーム内で共有してから運用してください。
        </WorkflowInfoBox>

        <WorkflowText sx={{ mt: 1 }}>
          proposal.md / business.md の作成が完了したら、次のパートに進む前に以下を確認してください。
        </WorkflowText>

        <WorkflowChecklist
          groups={[
            {
              title: 'proposal.md（例）',
              items: [
                'RFPの決定事項に記載した全テーブルが含まれている',
                '各テーブルのカラム・型・桁数・Not Null・PKがRFPと一致している',
                'インデックス定義が含まれている（必要なテーブルのみ）',
                'マスタテーブルの初期データが含まれている',
                'API一覧にRFPで決めた全APIが含まれている',
                '各APIの認可がRFPと一致している',
                'API概要のリクエスト/レスポンス/エラーが記載されている',
              ],
            },
            {
              title: 'business.md（例）',
              items: [
                'RFPで想定した全ユースケースがBUCとして記載されている',
                '各BUCに入力項目テーブルが含まれている（桁数・必須任意・形式）',
                '入力項目の桁数がproposalのテーブル定義と一致している',
                '利用APIのID・Endpointがproposalと一致している',
                '操作者（権限）がproposalの認可と一致している',
                '注意事項にユーザーが知るべき不可逆操作・副作用が記載されている',
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* 9. ベストプラクティス */}
      <WorkflowSection title="ベストプラクティス" id="section-9" number={9}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: '生成後は必ずセクション8「提出前チェックリスト」を通す',
              effect: '一括でも段階的でも品質を担保できる。チェックリスト通過が完了条件',
            },
            {
              practice: 'Steeringにフォーマットを定義してAIの出力構造を固定する',
              effect: '毎回同じ構造で出力され、品質がばらつかない',
            },
            {
              practice: '生成後にAIに「proposalとbusinessの整合性を確認して」と指示する',
              effect: '桁数・Endpoint・認可の食い違いをAIが自動検出してくれる',
            },
            {
              practice: '修正箇所が多い場合はファイル内注釈（note記法）を使う',
              effect: '位置の曖昧さがゼロになり、AIの修正精度が上がる',
            },
            {
              practice: 'proposalを修正したらbusinessも連動修正する',
              effect: '片方だけ直すと後工程でドキュメント間の矛盾が発覚する',
            },
            {
              practice: '提出前チェックリストを1項目ずつ人の目で確認する',
              effect: 'AIは正しさを保証しない。品質の最終責任は人間が持つ',
            },
            {
              practice: '参照チームが異なるならファイルを分離する',
              effect: '各チームが自分に必要なドキュメントだけを参照できる',
            },
          ]}
        />
      </WorkflowSection>

      {/* 10. アンチパターン */}
      <WorkflowSection title="アンチパターン" id="section-10" number={10}>
        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '一括生成してレビューせずに次のパートへ進む',
              effect: '後工程（API仕様書・実装・結合試験）で矛盾が発覚し大幅手戻り',
            },
            {
              practice: 'AIが出力したNot Nullや桁数を無確認で受け入れる',
              effect: 'RFPの決定事項と食い違ったまま後続に流れる',
            },
            {
              practice: 'API IDの採番をAIに任せる',
              effect: 'プロジェクト全体の採番ルールと衝突する',
            },
            {
              practice: 'proposalだけ修正してbusinessを放置する',
              effect: '画面チームが古い桁数・Endpointで実装し、結合試験で発覚する',
            },
            {
              practice: 'Steeringにフォーマットを定義せずに毎回プロンプトで指示する',
              effect: '生成のたびに構造がブレる。抜け漏れの温床になる',
            },
            {
              practice: 'チェックリストを形式的に流す（全部チェック付けるだけ）',
              effect: '品質保証にならない。後工程での手戻りコストが跳ね上がる',
            },
            {
              practice: '1ファイルに全チームの情報をまとめる',
              effect: '各チームが自分に関係ない部分を読み飛ばし、必要な情報を見落とす',
            },
          ]}
        />
      </WorkflowSection>

      {/* 11. 成果物まとめ */}
      <WorkflowSection title="成果物まとめ" id="section-11" number={11}>
        <WorkflowOutputBox>
          <WorkflowTable
            headers={['成果物', '説明', '利用先']}
            rows={[
              [
                '*.proposal.md',
                '技術的な決定事項（テーブル/API設計）',
                'バックエンド開発（スキーマ定義、API仕様書の入力）',
              ],
              [
                '*.business.md',
                'ビジネスユースケース（操作シナリオ）',
                '画面チームの操作手順書作成、シナリオ試験',
              ],
            ]}
          />
        </WorkflowOutputBox>
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowReqStep3;
